<template>
  <div class="">
    <!-- title -->
    <div>
      <h1 class="text-center text-base font-medium sm:text-lg pb-4">Setup your user profile</h1>
    </div>
    <form class="space-y-4" @submit.prevent>
      <Alert v-if="formErrorMessage" variant="destructive" class="mb-2">
        <AlertDescription>{{ formErrorMessage }}</AlertDescription>
      </Alert>
      <!-- email (read-only) so the invitee knows who is signing up -->
      <div class="grid gap-1.5">
        <label class="text-sm font-medium text-muted-foreground" for="inviteEmail">Signing up as:</label>
        <Input
          id="inviteEmail"
          :model-value="inviteEmail"
          placeholder="Loading…"
          name="email"
          type="email"
          autocomplete="email"
          readonly
          class="bg-muted text-muted-foreground cursor-not-allowed"
        />
      </div>

      <Input
        v-model="createProfileRequest.firstName"
        placeholder="First name"
        name="firstName"
        type="text"
        autocomplete="given-name"
      />
      <Input
        v-model="createProfileRequest.lastName"
        placeholder="Last name"
        name="lastName"
        type="text"
        autocomplete="family-name"
      />

      <!-- password input -->
      <Input
        id="passwordInput"
        v-model="createProfileRequest.password"
        placeholder="Password"
        name="new-password"
        type="password"
        autocomplete="new-password"
      />
      <Input
        id="confirmPasswordInput"
        v-model="confirmPassword"
        placeholder="Confirm password"
        name="confirm-password"
        type="password"
        autocomplete="new-password"
      />

      <p class="text-xs">
        By creating an account, you consent to the processing of your data in accordance with our
        data processing policies as outlined in our Terms of Service agreement.
      </p>

      <!-- sign in button -->
      <Button
        id="signInButton"
        type="submit"
        :disabled="!secure || !matching"
        @click="signUp()"
        class="w-full h-10"
      >
        Create account
      </Button>

      <div class="text-muted-foreground text-sm">
        <h2 class="font-medium">Password requirements</h2>
        <p>Please follow this guide for a strong password</p>
        <p :class="emailStrong.special ? 'text-blue-600 font-medium' : 'text-muted-foreground'">
          - One special character
        </p>
        <p :class="emailStrong.length ? 'text-blue-600 font-medium' : 'text-muted-foreground'">
          - Min 8 characters
        </p>
        <p :class="emailStrong.number ? 'text-blue-600 font-medium' : 'text-muted-foreground'">
          - One number (2 recommended)
        </p>
        <p :class="emailStrong.capital ? 'text-blue-600 font-medium' : 'text-muted-foreground'">
          - One capital
        </p>
        <p :class="matching ? 'text-blue-600 font-medium' : 'text-muted-foreground'">- Matching</p>
      </div>
    </form>
  </div>
</template>

<script>
import { Input, Button, Alert, AlertDescription } from '@boogi/ui'

export default {
  name: 'SignIn',
  components: {
    Input,
    Button,
    Alert,
    AlertDescription,
  },
  props: {
    backdoorKey: {
      type: String,
    },
  },
  data() {
    return {
      createProfileRequest: {
        inviteId: this.$route.params.id,
        firstName: '',
        lastName: '',
        password: '',
      },
      confirmPassword: '',
      inviteEmail: '',

      emailStrong: {
        special: false,
        length: false,
        number: false,
        capital: false,
      },

      loading: false,
      formErrorMessage: '',
      showErrorModal: false,
      errors: {
        type: Object,
        default: function () {
          return {
            email: '',
            password: '',
          }
        },
      },
    }
  },
  created() {
    this.getInviteEmail()
  },
  methods: {
    closeModal() {
      this.showErrorModal = false
    },

    async getInviteEmail() {
      try {
        const response = await this.$apiFetch(
          `${process.env.VUE_APP_API_URL}/private/api/client/auth/invite/${this.createProfileRequest.inviteId}`,
          { method: 'GET', headers: { 'Content-Type': 'application/json' } }
        )
        if (response.ok) {
          const object = await response.json().catch(() => ({}))
          this.inviteEmail = object.email || ''
        }
      } catch (error) {
        return
      }
    },

    async signUp() {
      try {
        const response = await this.$apiFetch(
          `${process.env.VUE_APP_API_URL}/private/api/client/auth/accept-invite`,
          {
            method: 'POST',
            body: JSON.stringify(this.createProfileRequest),
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )

        if (response.ok) {
          const object = await response.json().catch(() => ({}))

          if (object && object.status === 'MFA_SETUP') {
            sessionStorage.setItem(
              'mfaSession',
              JSON.stringify({
                session: object.session,
                email: object.email,
                type: 'MFA_SETUP',
                qrCodeValue: object.qrcodeData || object.QRCodeData || object.qRCodeData,
              })
            )
            this.$router.push('/auth/two-factor')
          } else if (object && object.status === 'MFA_REQUIRED') {
            sessionStorage.setItem(
              'mfaSession',
              JSON.stringify({
                session: object.session,
                email: object.email,
                type: 'MFA_CODE',
              })
            )
            this.$router.push('/auth/two-factor')
          } else if (object && object.status === 'COMPLETED' && object.idToken) {
            localStorage.setItem('token', 'Bearer ' + object.idToken)
            this.$router.push('/')
          } else {
            this.$router.push('/auth/sign-in?type=newUser')
          }
        } else {
          this.formErrorMessage = await this.extractErrorMessage(response)
        }
      } catch (error) {
        this.formErrorMessage = 'Could not reach the server. Please check your connection and try again.'
        return
      }
    },

    async extractErrorMessage(response) {
      try {
        const text = await response.text()
        if (!text) {
          return this.fallbackMessageForStatus(response.status)
        }
        // Backend may return JSON ({ message: ... }) or plain text ("Bad Request: ...").
        try {
          const json = JSON.parse(text)
          const msg = json.message || json.error || ''
          if (msg) return msg.replace(/^Bad Request:\s*/i, '').trim()
        } catch (_) {
          // Not JSON, fall through to text handling.
        }
        return text.replace(/^Bad Request:\s*/i, '').trim() || this.fallbackMessageForStatus(response.status)
      } catch (_) {
        return this.fallbackMessageForStatus(response.status)
      }
    },

    fallbackMessageForStatus(status) {
      if (status === 400) return 'Invitation is invalid or has timed out'
      if (status === 401 || status === 403) return 'You are not authorised to accept this invitation'
      if (status >= 500) return 'Something went wrong on our end. Please try again shortly.'
      return 'Could not accept invitation. Please try again.'
    },
  },
  computed: {
    matching() {
      return (
        this.confirmPassword === this.createProfileRequest.password &&
        this.createProfileRequest.password != '' &&
        this.createProfileRequest.firstName != '' &&
        this.createProfileRequest.lastName != ''
      )
    },
    secure() {
      return (
        this.emailStrong.length &&
        this.emailStrong.special &&
        this.emailStrong.number &&
        this.emailStrong.capital
      )
    },
  },
  mounted() {
    document.title = 'New user'
  },
  watch: {
    createProfileRequest: {
      deep: true,
      handler: function (val) {
        if (val.password.match(/^(?=.*[A-Z])/)) {
          this.emailStrong.capital = true
        } else {
          this.emailStrong.capital = false
        }
        if (val.password.match(/^(?=.*[0-9])/)) {
          this.emailStrong.number = true
        } else {
          this.emailStrong.number = false
        }
        if (val.password.match(/^(?=.*[!@#$%^&*-])/)) {
          this.emailStrong.special = true
        } else {
          this.emailStrong.special = false
        }
        if (val.password.match(/^(?=.{8,})/)) {
          this.emailStrong.length = true
        } else {
          this.emailStrong.length = false
        }
      },
    },
  },
}
</script>

<style></style>
